import React, { useState } from "react";
import {
  FaEye, FaEyeSlash, FaLock, FaShieldAlt, FaCheckCircle,
  FaExclamationTriangle, FaSpinner, FaArrowLeft, FaKey, FaSave
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ReporterChangePassword = () => {
  const [oldPassword, setOldPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [showApproveSpinner, setShowApproveSpinner] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false, uppercase: false, lowercase: false, number: false, special: false
  });

  const checkPasswordStrength = (password) => {
    const requirements = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    setPasswordRequirements(requirements);
    setPasswordStrength(Object.values(requirements).filter(Boolean).length);
  };

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
    checkPasswordStrength(e.target.value);
  };

  const strengthColor = passwordStrength <= 2 ? "bg-red-500" : passwordStrength <= 3 ? "bg-yellow-500" : passwordStrength <= 4 ? "bg-blue-500" : "bg-green-500";
  const strengthText = passwordStrength <= 2 ? "Weak" : passwordStrength <= 3 ? "Fair" : passwordStrength <= 4 ? "Good" : "Strong";
  const strengthTextColor = passwordStrength <= 2 ? "text-red-500" : passwordStrength <= 3 ? "text-yellow-500" : passwordStrength <= 4 ? "text-blue-500" : "text-green-500";

  const handleVerifyOldPassword = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/password/verify-old`,
        { oldPassword },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (res.status === 200) { setStep(2); setMessage(""); }
      else { toast.error("Old password is incorrect."); setMessage("Old password is incorrect."); }
    } catch (error) {
      toast.error("Old password is incorrect.");
      setMessage("Old password is incorrect.");
    }
    setLoading(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmNewPassword) {
      toast.error("Passwords do not match!"); return;
    }
    setLoading(true); setShowApproveSpinner(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/user/password/update`,
        { newPassword, confirmNewPassword },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );
      if (res.status === 200) {
        toast.success("Password changed successfully!");
        setMessage("success");
        setStep(1); setOldPassword(""); setNewPassword(""); setConfirmNewPassword("");
      } else { toast.error("Failed to change password."); setMessage("Failed to change password."); }
    } catch (error) {
      toast.error("Error changing password."); setMessage("Error changing password.");
    }
    setLoading(false); setShowApproveSpinner(false);
  };

  const reqItems = [
    { key: "length", label: "At least 8 characters" },
    { key: "uppercase", label: "One uppercase letter" },
    { key: "lowercase", label: "One lowercase letter" },
    { key: "number", label: "One number" },
    { key: "special", label: "One special character" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Compact Header */}
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-4 pt-6 pb-14 text-white">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
            <FaLock className="w-4 h-4 text-indigo-300" />
          </div>
          <div className="flex-1">
            <h1 className="text-base font-black text-white">Change Password</h1>
            <p className="text-indigo-300 text-xs">
              {step === 1 ? "Verify your current password first" : "Set a new secure password"}
            </p>
          </div>
          {step === 2 && (
            <button
              onClick={() => { setStep(1); setMessage(""); }}
              className="flex items-center gap-1.5 text-xs font-bold text-white/60 hover:text-white transition-colors"
            >
              <FaArrowLeft className="w-3 h-3" /> Back
            </button>
          )}
        </div>
      </div>

      {/* Main Card — overlaps header */}
      <div className="max-w-md mx-auto px-3 -mt-8 pb-8">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">

          {/* Step Indicators */}
          <div className="flex border-b border-slate-100">
            {[1, 2].map(s => (
              <div key={s} className={`flex-1 py-2.5 text-center text-[10px] font-black uppercase tracking-widest transition-colors
                ${step === s ? "text-indigo-600 bg-indigo-50" : "text-slate-400 bg-white"}`}>
                {s === 1 ? "1. Verify" : "2. New Password"}
              </div>
            ))}
          </div>

          <div className="p-5">
            {/* Step 1 */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    Current Password
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <FaKey className="w-3.5 h-3.5" />
                    </div>
                    <input
                      type={showOldPassword ? "text" : "password"}
                      value={oldPassword}
                      onChange={(e) => setOldPassword(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && oldPassword && handleVerifyOldPassword()}
                      className="w-full pl-9 pr-10 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      onClick={() => setShowOldPassword(p => !p)}
                    >
                      {showOldPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {message && (
                  <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                    <FaExclamationTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                    {message}
                  </div>
                )}

                <button
                  onClick={handleVerifyOldPassword}
                  disabled={loading || !oldPassword}
                  className="w-full bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-black hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaShieldAlt className="w-4 h-4" />}
                  {loading ? "Verifying..." : "Verify & Continue"}
                </button>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="space-y-4">
                {/* New Password */}
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <FaLock className="w-3.5 h-3.5" />
                    </div>
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={handleNewPasswordChange}
                      className="w-full pl-9 pr-10 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                      placeholder="Enter new password"
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowNewPassword(p => !p)}>
                      {showNewPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                    </button>
                  </div>

                  {/* Strength Bar */}
                  {newPassword && (
                    <div className="mt-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Strength</span>
                        <span className={`text-[9px] font-black uppercase tracking-widest ${strengthTextColor}`}>{strengthText}</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full transition-all duration-300 ${strengthColor}`} style={{ width: `${(passwordStrength / 5) * 100}%` }} />
                      </div>
                    </div>
                  )}
                </div>

                {/* Requirements */}
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Requirements</p>
                  <div className="grid grid-cols-1 gap-1">
                    {reqItems.map(r => (
                      <div key={r.key} className={`flex items-center gap-2 text-[10px] font-bold ${passwordRequirements[r.key] ? "text-green-600" : "text-slate-400"}`}>
                        <FaCheckCircle className={`w-3 h-3 flex-shrink-0 ${passwordRequirements[r.key] ? "text-green-500" : "text-slate-300"}`} />
                        {r.label}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      <FaLock className="w-3.5 h-3.5" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="w-full pl-9 pr-10 py-2.5 text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
                      placeholder="Confirm new password"
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setShowConfirmPassword(p => !p)}>
                      {showConfirmPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                    </button>
                  </div>
                  {confirmNewPassword && (
                    <p className={`mt-1.5 text-[10px] font-bold flex items-center gap-1.5 ${newPassword === confirmNewPassword ? "text-green-600" : "text-red-500"}`}>
                      {newPassword === confirmNewPassword
                        ? <><FaCheckCircle className="w-3 h-3" /> Passwords match</>
                        : <><FaExclamationTriangle className="w-3 h-3" /> Passwords do not match</>}
                    </p>
                  )}
                </div>

                {message && message !== "success" && (
                  <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                    <FaExclamationTriangle className="w-3.5 h-3.5 flex-shrink-0" /> {message}
                  </div>
                )}

                <button
                  onClick={handleChangePassword}
                  disabled={loading || !newPassword || !confirmNewPassword || newPassword !== confirmNewPassword || passwordStrength < 3}
                  className="w-full bg-indigo-600 text-white py-2.5 rounded-xl text-sm font-black hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaSave className="w-4 h-4" />}
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </div>
            )}
          </div>

          {/* Security Tips Footer */}
          <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/60">
            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Security Tips</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {["Use a unique password", "Never share it", "Update regularly"].map(tip => (
                <span key={tip} className="flex items-center gap-1 text-[10px] text-slate-500 font-bold">
                  <FaCheckCircle className="w-2.5 h-2.5 text-green-500" /> {tip}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {showApproveSpinner && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-6 text-center max-w-xs mx-4">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm font-black text-slate-900">Updating Password...</p>
            <p className="text-xs text-slate-500 mt-1">Securing your account</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReporterChangePassword;
